import {install as installHotkey, uninstall as uninstallHotkey} from '@github-ui/hotkey'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {HomeIcon, MarkGithubIcon, XIcon} from '@primer/octicons-react'
import {
  Dialog,
  type DialogHeaderProps,
  type DialogProps,
  IconButton,
  NavList,
  type NavListItemProps,
  Stack,
  VisuallyHidden,
} from '@primer/react'
import {useEffect, useMemo, useRef} from 'react'

import {useGlobalNavAnalytics} from '../utils/Analytics'

export type ValidPath<T extends string = string> = `/${T}` | `/`

export type SideNavMenuItemProps = {
  /** URL that the menu item links to */
  href: ValidPath

  /** Icon component to display alongside the menu item */
  icon: typeof HomeIcon

  /** Text label for the menu item */
  label: string

  /** Unique key for analytics tracking */
  analyticsKey: string

  /** Optional hotkey to trigger this menu item */
  hotkey?: string

  /** Additional class names to apply to the menu item */
  className?: string

  /** Additional options for active path matching */
  activePath?: {
    /** Additional pathnames that should be considered active for this menu item */
    additionalPathnames?: ValidPath[]

    /**
     * Whether to match subpaths of the href
     * Ex. if href is '/issues' and includeSubpaths is true, '/issues/123' would be considered active
     */
    includeSubpaths?: boolean
  }
} & Omit<NavListItemProps, 'children'>

function normalizePathEnding(path: string): string {
  return path.endsWith('/') ? path : `${path}/`
}

export function isMenuItemActive(
  currentLocation: typeof ssrSafeLocation,
  href: string,
  activePath?: SideNavMenuItemProps['activePath'],
): boolean {
  if (!href || !currentLocation?.origin) {
    return false
  }

  const targetUrl = new URL(href, currentLocation.origin)

  // Normalize href to always end with a slash for subpath matching
  // Otherwise "/test" would match "/testing"
  const targetPath = normalizePathEnding(targetUrl.pathname)
  const currentPath = normalizePathEnding(currentLocation.pathname) as ValidPath

  if (currentPath === targetPath) {
    return true
  }

  // Only match subpaths if the href is not the root path
  if (targetPath !== '/' && activePath?.includeSubpaths && currentPath.startsWith(targetPath)) {
    return true
  }

  if (activePath?.additionalPathnames?.includes(currentPath)) {
    return true
  }

  return false
}

export function SideNavMenuItem({
  href,
  icon: Icon,
  label,
  analyticsKey,
  hotkey,
  activePath,
  ...rest
}: SideNavMenuItemProps) {
  const {sendItemClick} = useGlobalNavAnalytics()
  const itemRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    const {current: menuItemElement} = itemRef

    if (hotkey && menuItemElement) {
      if (document.querySelector(`[data-hotkey="${hotkey}"]`)) {
        // hotkey is already used on the page, so avoid registering it again
        return
      }

      installHotkey(menuItemElement, hotkey)

      return () => {
        uninstallHotkey(menuItemElement)
      }
    }
  }, [hotkey])

  const isActiveRoute = useMemo<boolean>(() => {
    return Boolean(href) && isMenuItemActive(ssrSafeLocation, href, activePath)
  }, [activePath, href])

  return (
    <NavList.Item
      ref={itemRef}
      href={href}
      data-testid={`side-nav-menu-item-${analyticsKey}`}
      aria-current={isActiveRoute ? 'page' : undefined}
      onClick={() => {
        sendItemClick(analyticsKey)
      }}
      {...rest}
    >
      <NavList.LeadingVisual>
        <Icon />
      </NavList.LeadingVisual>
      {label}
    </NavList.Item>
  )
}

export type SideNavMenuProps = React.PropsWithChildren<Pick<DialogProps, 'onClose' | 'returnFocusRef'>>

function SideNavMenuHeader(props: DialogHeaderProps) {
  const {sendItemClick} = useGlobalNavAnalytics()

  return (
    <Stack align="stretch" direction="horizontal" justify="space-between" padding="normal">
      <VisuallyHidden>
        <h2 id={props.dialogLabelId}>Global Navigation Menu</h2>
      </VisuallyHidden>
      <MarkGithubIcon size="medium" />
      <IconButton
        size="medium"
        variant="invisible"
        icon={XIcon}
        onClick={() => {
          sendItemClick('CLOSE_MENU')
          props.onClose('close-button')
        }}
        aria-label="Close menu"
      />
    </Stack>
  )
}

function DialogBody(props: React.PropsWithChildren) {
  return <div className="p-2">{props.children}</div>
}

export function SideNavMenu(props: SideNavMenuProps) {
  const {onClose} = props

  return (
    <Dialog onClose={onClose} width="medium" position="left" renderHeader={SideNavMenuHeader} renderBody={DialogBody}>
      <NavList>{props.children}</NavList>
    </Dialog>
  )
}

try{ SideNavMenuItem.displayName ||= 'SideNavMenuItem' } catch {}
try{ SideNavMenuHeader.displayName ||= 'SideNavMenuHeader' } catch {}
try{ DialogBody.displayName ||= 'DialogBody' } catch {}
try{ SideNavMenu.displayName ||= 'SideNavMenu' } catch {}